import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import UpdateIcon from '@mui/icons-material/Update';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EventIcon from '@mui/icons-material/Event';
import BarChartIcon from '@mui/icons-material/BarChart';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SearchIcon from '@mui/icons-material/Search';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from 'react-i18next';
import { tableRowActionsStyles } from './TableRowActions.styles';

const TableRowActions = ({
  row,
  rowIndex,
  actions = [],
  onAction,
  size = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const open = Boolean(anchorEl);

  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = event => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleActionClick = (event, action) => {
    event.stopPropagation();
    handleClose();

    // If action has a custom onClick function, use it
    if (action.onClick) {
      action.onClick(row, rowIndex);
      return;
    }

    // Otherwise, use the default onAction handler
    if (onAction) {
      onAction(action.type, row, rowIndex);
    }
  };

  const getActionIcon = (type, action) => {
    // If action has a custom icon, use it
    if (action?.icon) {
      if (typeof action.icon === 'string') {
        const iconMap = {
          'groups': GroupsIcon,
          'visibility': VisibilityIcon,
          'assignment': AssignmentIcon,
          'assignment_ind': AssignmentIndIcon,
          'update': UpdateIcon,
          'edit_note': EditNoteIcon,
          'event': EventIcon,
          'bar_chart': BarChartIcon,
          'open_in_new': OpenInNewIcon,
          'manage_accounts': ManageAccountsIcon,
          'search': SearchIcon,
          'autorenew': AutorenewIcon,
        };

        const IconComponent = iconMap[action.icon];
        if (IconComponent) {
          return <IconComponent fontSize="small" />;
        }

        return (
          <span
            style={{
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {action.icon}
          </span>
        );
      }
      return action.icon;
    }

    switch (type) {
      case 'edit':
        return (
          <EditIcon fontSize="small" sx={tableRowActionsStyles.editIcon} />
        );
      case 'delete':
        return (
          <DeleteIcon fontSize="small" sx={tableRowActionsStyles.deleteIcon} />
        );
      case 'view':
        return (
          <VisibilityIcon
            fontSize="small"
            sx={tableRowActionsStyles.viewIcon}
          />
        );
      default:
        return null;
    }
  };

  const getMenuItemStyle = action => {
    // First check by color, then by type
    if (action.color === 'error') {
      return tableRowActionsStyles.errorMenuItem;
    }
    if (action.color === 'success') {
      return tableRowActionsStyles.successMenuItem;
    }
    if (action.color === 'info') {
      return tableRowActionsStyles.editMenuItem; // Use edit style for info color
    }
    if (action.color === 'warning') {
      return tableRowActionsStyles.editMenuItem; // Use edit style for warning color
    }
    if (action.color === 'primary') {
      return tableRowActionsStyles.editMenuItem; // Use edit style for primary color
    }

    // Then check by type
    switch (action.type) {
      case 'edit':
        return tableRowActionsStyles.editMenuItem;
      case 'delete':
        return tableRowActionsStyles.errorMenuItem;
      case 'view':
        return tableRowActionsStyles.viewMenuItem;
      case 'approve':
        return tableRowActionsStyles.successMenuItem;
      case 'reject':
        return tableRowActionsStyles.errorMenuItem;
      default:
        return tableRowActionsStyles.menuItem;
    }
  };

  const renderActions = () => {
    const items = [];

    actions.forEach((action, index) => {
      // Check if action should be shown
      if (action.showIf && !action.showIf(row, rowIndex)) {
        return;
      }

      if (action.divider && index > 0) {
        items.push(
          <Divider
            key={`divider-${index}`}
            sx={tableRowActionsStyles.divider}
          />
        );
      }

      items.push(
        <MenuItem
          key={action.key || action.type || index}
          onClick={e => handleActionClick(e, action)}
          disabled={action.disabled}
          sx={getMenuItemStyle(action)}
        >
          <ListItemIcon sx={tableRowActionsStyles.listItemIcon}>
            {getActionIcon(action.type, action)}
          </ListItemIcon>
          <ListItemText
            primary={action.label && action.label.includes('.') ? t(action.label) : action.label}
            primaryTypographyProps={tableRowActionsStyles.listItemText}
          />
        </MenuItem>
      );
    });

    return items;
  };

  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        size={size}
        onClick={handleClick}
        aria-label="more actions"
        sx={tableRowActionsStyles.iconButton}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: tableRowActionsStyles.menu,
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          sx: tableRowActionsStyles.menuListProps,
        }}
        transitionDuration={tableRowActionsStyles.transitionDuration}
        slotProps={{
          paper: {
            elevation: 8,
            sx: tableRowActionsStyles.slotPaperProps,
          },
        }}
      >
        {renderActions()}
      </Menu>
    </>
  );
};

export default TableRowActions;
